<script>
/* eslint-disable @intlify/vue-i18n/no-raw-text */
import { mapGetters } from 'vuex';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ComboBox,
    TagMultiSelectComboBox,
    Editor,
    // eslint-disable-next-line vue/no-reserved-component-names
    Button,
  },
  props: {
    show: Boolean,
    deal: Object,
    pipelineId: Number,
    stages: Array,
    initialValues: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      localDeal: {
        title: '',
        pipeline_stage_id: '',
        priority: 'none',
        close_date: null,
        start_date: null,
        description: '',
        pipeline_id: null,
        contact_id: null,
        conversation_id: null,
        responsible_agent_ids: [],
        label_list: [],
      },
      selectedProducts: [],
      productSearch: '',
      couponCode: '',
      selectedCoupon: null,
      manualDiscount: 0,
      surcharge: 0,
      shipping: 0,
      showProductsModal: false,
      productsView: 'summary',
      showCreateDealProductForm: false,
      dealNewProduct: {
        name: '',
        sku: '',
        price: '',
      },
      productsDraftSnapshot: null,
      productsModalSaving: false,
      showAttributesModal: false,
      localCustomAttributes: {},
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    contacts() {
      return this.$store.getters['contacts/getContacts'] || [];
    },
    allPipelines() {
      return this.$store.getters['pipelines/getPipelines'] || [];
    },
    products() {
      return this.$store.getters['products/getProducts'] || [];
    },
    coupons() {
      return this.$store.getters['coupons/getCoupons'] || [];
    },
    dealAttributeDefinitions() {
      const getter = this.$store.getters['attributes/getAttributesByModel'];
      if (typeof getter !== 'function') return [];
      return getter('deal_attribute') || [];
    },
    allConversations() {
      return this.$store.getters.getAllConversations || [];
    },
    assigneeOptions() {
      const agents = this.$store.getters['agents/getAgents'] || [];
      return agents.map(agent => ({
        value: agent.id,
        label: agent.name,
      }));
    },
    labelOptions() {
      const labels = this.$store.getters['labels/getLabels'] || [];
      return labels.map(label => ({
        value: label.title,
        label: label.title,
        color: label.color || label.hex_color || label.text_color || '#3b82f6',
      }));
    },
    priorityOptions() {
      return [
        {
          value: 'urgent',
          icon: 'i-lucide-chevrons-up',
          color: '#dc2626',
          label: this.$t('DEALS.FORM.PRIORITY.OPTIONS.URGENT'),
        },
        {
          value: 'high',
          icon: 'i-lucide-chevron-up',
          color: '#eab308',
          label: this.$t('DEALS.FORM.PRIORITY.OPTIONS.HIGH'),
        },
        {
          value: 'medium',
          icon: 'i-lucide-equal',
          color: '#3b82f6',
          label: this.$t('DEALS.FORM.PRIORITY.OPTIONS.MEDIUM'),
        },
        {
          value: 'low',
          icon: 'i-lucide-chevron-down',
          color: '#64748b',
          label: this.$t('DEALS.FORM.PRIORITY.OPTIONS.LOW'),
        },
        {
          value: 'none',
          icon: 'i-lucide-minus',
          color: '#64748b',
          label: this.$t('DEALS.FORM.PRIORITY.OPTIONS.NONE'),
        },
      ];
    },
    pipelineOptions() {
      return this.allPipelines.map(pipeline => ({
        value: pipeline.id,
        label: pipeline.name,
      }));
    },
    stageOptions() {
      const selected = this.allPipelines.find(
        pipeline => Number(pipeline.id) === Number(this.localDeal.pipeline_id)
      );
      const stages = selected?.stages || this.stages || [];
      return stages.map(stage => ({
        value: stage.id,
        label: stage.name,
      }));
    },
    contactOptions() {
      const options = this.contacts.map(contact => ({
        value: contact.id,
        label: contact.name || contact.email || `Contato #${contact.id}`,
      }));
      const selectedId = Number(this.localDeal.contact_id);
      if (
        selectedId &&
        !options.some(option => Number(option.value) === selectedId)
      ) {
        options.unshift({
          value: selectedId,
          label: this.resolveContactFallbackLabel(selectedId),
        });
      }
      return options;
    },
    conversationOptions() {
      const allowedStatuses = new Set([
        'open',
        'resolved',
        'pending',
        'snoozed',
      ]);
      const sourceConversations = this.localDeal.contact_id
        ? this.$store.getters['contactConversations/getContactConversation'](
            this.localDeal.contact_id
          ) || []
        : this.allConversations;

      const options = sourceConversations
        .filter(conv => {
          if (!allowedStatuses.has(conv.status)) return false;
          return true;
        })
        .map(conv => ({
          value: conv.id,
          label: `#${conv.id} - ${conv?.meta?.sender?.name || conv?.meta?.sender?.identifier || 'Conversa'}`,
        }));
      const selectedId = Number(this.localDeal.conversation_id);
      if (
        selectedId &&
        !options.some(option => Number(option.value) === selectedId)
      ) {
        const dealConversationLabel = this.deal?.contact?.name
          ? `#${selectedId} - ${this.deal.contact.name}`
          : null;
        options.unshift({
          value: selectedId,
          label:
            this.initialValues?.conversation_label ||
            dealConversationLabel ||
            `#${selectedId} - Conversa`,
        });
      }
      return options;
    },
    activeProducts() {
      return this.products.filter(product => product.active !== false);
    },
    filteredCatalogProducts() {
      const term = this.productSearch.trim().toLowerCase();
      const selectedIds = new Set(
        this.selectedProducts.map(item => Number(item.product_id))
      );
      return this.activeProducts.filter(product => {
        if (selectedIds.has(Number(product.id))) return false;
        if (!term) return true;
        return (
          product.name?.toLowerCase().includes(term) ||
          product.default_variant?.sku?.toLowerCase().includes(term)
        );
      });
    },
    subtotalProducts() {
      return this.selectedProducts.reduce(
        (total, item) =>
          total + Number(item.quantity || 0) * Number(item.unit_price || 0),
        0
      );
    },
    couponDiscount() {
      if (!this.selectedCoupon) return 0;
      if (this.selectedCoupon.discount_type === 'percentage') {
        return (
          (this.subtotalProducts * Number(this.selectedCoupon.value || 0)) / 100
        );
      }
      return Number(this.selectedCoupon.value || 0);
    },
    effectiveDiscount() {
      return Number(this.manualDiscount || 0) > 0
        ? Number(this.manualDiscount || 0)
        : this.couponDiscount;
    },
    finalTotal() {
      const total =
        this.subtotalProducts -
        this.effectiveDiscount +
        Number(this.surcharge || 0) +
        Number(this.shipping || 0);
      return total > 0 ? total : 0;
    },
    canSaveProductsModal() {
      const hasProducts = this.selectedProducts.length > 0;
      const hasCoupon = String(this.couponCode || '').trim().length > 0;
      const hasAdjustments =
        Number(this.manualDiscount || 0) > 0 ||
        Number(this.surcharge || 0) > 0 ||
        Number(this.shipping || 0) > 0;
      return hasProducts || hasCoupon || hasAdjustments;
    },
    canQuickCreateProduct() {
      return (
        String(this.dealNewProduct.name || '').trim().length > 0 &&
        String(this.dealNewProduct.sku || '').trim().length > 0
      );
    },
    canSubmitDeal() {
      return (
        String(this.localDeal.title || '').trim().length > 0 &&
        !!this.localDeal.pipeline_id &&
        !!this.localDeal.pipeline_stage_id &&
        !!this.localDeal.contact_id
      );
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.$store
            .dispatch('pipelines/get', {
              accountId: this.currentAccountId,
            })
            .catch(() => null);
          this.$store.dispatch('agents/get');
          this.$store.dispatch('labels/get').catch(() => null);
          this.$store
            .dispatch('products/get', {
              accountId: this.currentAccountId,
            })
            .catch(() => null);
          this.$store
            .dispatch('coupons/get', {
              accountId: this.currentAccountId,
            })
            .catch(() => null);

          if (!this.deal) {
            this.resetForm();
            this.applyInitialValues();
          }
        }
      },
    },
    deal: {
      immediate: true,
      handler(newDeal) {
        if (newDeal) {
          this.localDeal = {
            title: newDeal.title || '',
            pipeline_stage_id: newDeal.pipeline_stage_id || '',
            priority:
              newDeal.priority ||
              newDeal?.custom_attributes?.priority ||
              'none',
            close_date: newDeal.close_date
              ? newDeal.close_date.split('T')[0]
              : null,
            start_date: newDeal?.custom_attributes?.start_date
              ? String(newDeal.custom_attributes.start_date).split('T')[0]
              : null,
            description: newDeal.description || '',
            pipeline_id: newDeal.pipeline_id || this.pipelineId,
            contact_id: newDeal.contact_id || null,
            conversation_id:
              newDeal.conversation_display_id ||
              newDeal.conversation_id ||
              null,
            responsible_agent_ids: Array.isArray(
              newDeal?.custom_attributes?.responsible_agent_ids
            )
              ? newDeal.custom_attributes.responsible_agent_ids
              : [],
            label_list: (() => {
              if (Array.isArray(newDeal.labels)) return newDeal.labels;
              if (Array.isArray(newDeal.label_list)) return newDeal.label_list;
              return [];
            })(),
          };
          this.selectedProducts = (newDeal.products || []).map(item => ({
            deal_product_id: item.id || null,
            product_id: item.product_id,
            name: item.name,
            sku: item.sku,
            quantity: Number(item.quantity || 1),
            unit_price: Number(item.unit_price || 0),
          }));
          const attrs = newDeal.custom_attributes || {};
          this.couponCode = attrs.coupon_code || '';
          this.selectedCoupon =
            this.coupons.find(
              coupon =>
                coupon.code?.toUpperCase() ===
                String(this.couponCode || '').toUpperCase()
            ) || null;
          this.manualDiscount = Number(attrs.manual_discount || 0);
          this.surcharge = Number(attrs.surcharge || 0);
          this.shipping = Number(attrs.shipping || 0);
          if (this.localDeal.contact_id) {
            this.$store.dispatch(
              'contactConversations/get',
              this.localDeal.contact_id
            );
          }
        } else {
          this.resetForm();
          this.applyInitialValues();
        }
      },
    },
    pipelineId: {
      immediate: true,
      handler(newPipelineId) {
        if (newPipelineId && !this.deal) {
          this.localDeal.pipeline_id = newPipelineId;
          if (
            this.stages &&
            this.stages.length > 0 &&
            !this.localDeal.pipeline_stage_id
          ) {
            this.localDeal.pipeline_stage_id = this.stages[0].id;
          }
        }
      },
    },
    'localDeal.pipeline_id'(pipelineId) {
      if (!pipelineId) return;
      const firstStage = this.stageOptions[0];
      if (!firstStage) {
        this.localDeal.pipeline_stage_id = null;
        return;
      }
      const hasCurrent = this.stageOptions.some(
        option =>
          Number(option.value) === Number(this.localDeal.pipeline_stage_id)
      );
      if (!hasCurrent) {
        this.localDeal.pipeline_stage_id = firstStage.value;
      }
    },
    initialValues: {
      deep: true,
      handler() {
        if (this.show && !this.deal) {
          this.applyInitialValues();
        }
      },
    },
  },
  methods: {
    toSafeNumber(value, { min = null } = {}) {
      const parsed = Number(value);
      let normalized = Number.isFinite(parsed) ? parsed : 0;
      if (min !== null) {
        normalized = Math.max(min, normalized);
      }
      return normalized;
    },
    applyInitialValues() {
      const initial = this.initialValues || {};
      if (!Object.keys(initial).length) return;

      this.localDeal = {
        ...this.localDeal,
        title: initial.title ?? this.localDeal.title,
        description: initial.description ?? this.localDeal.description,
        pipeline_id: initial.pipeline_id ?? this.localDeal.pipeline_id,
        pipeline_stage_id:
          initial.pipeline_stage_id ?? this.localDeal.pipeline_stage_id,
        contact_id: initial.contact_id ?? this.localDeal.contact_id,
        conversation_id:
          initial.conversation_id ?? this.localDeal.conversation_id,
        close_date: initial.close_date ?? this.localDeal.close_date,
        start_date: initial.start_date ?? this.localDeal.start_date,
        priority: initial.priority ?? this.localDeal.priority,
        responsible_agent_ids: Array.isArray(initial.responsible_agent_ids)
          ? initial.responsible_agent_ids
          : this.localDeal.responsible_agent_ids,
        label_list: Array.isArray(initial.label_list)
          ? initial.label_list
          : this.localDeal.label_list,
      };

      if (this.localDeal.contact_id) {
        this.$store.dispatch(
          'contactConversations/get',
          this.localDeal.contact_id
        );
      }
    },
    resolveContactFallbackLabel(contactId) {
      const selectedId = Number(contactId);
      const getContact = this.$store.getters['contacts/getContact'];
      const storeContact =
        typeof getContact === 'function' ? getContact(selectedId) : null;
      return (
        this.initialValues?.contact_label ||
        this.deal?.contact?.name ||
        this.deal?.contact?.email ||
        storeContact?.name ||
        storeContact?.email ||
        `Contato #${selectedId}`
      );
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value || 0));
    },
    addProduct(product) {
      this.selectedProducts.push({
        deal_product_id: null,
        product_id: product.id,
        name: product.name,
        sku: product.default_variant?.sku || '',
        quantity: 1,
        unit_price: Number(product.default_variant?.price || 0),
      });
    },
    addProductToProductsModal(product) {
      if (!product?.id) return;
      this.addProduct(product);
      this.productsView = 'summary';
    },
    removeProduct(productId) {
      this.selectedProducts = this.selectedProducts.filter(
        item => Number(item.product_id) !== Number(productId)
      );
    },
    increaseProductQty(item) {
      item.quantity = Number(item.quantity || 0) + 1;
    },
    decreaseProductQty(item) {
      if (Number(item.quantity || 0) <= 1) {
        this.removeProduct(item.product_id);
        return;
      }
      item.quantity = Number(item.quantity || 0) - 1;
    },
    applyCoupon() {
      const normalized = String(this.couponCode || '')
        .trim()
        .toUpperCase();
      if (!normalized) {
        this.selectedCoupon = null;
        return;
      }
      this.selectedCoupon =
        this.coupons.find(
          coupon =>
            coupon.code?.toUpperCase() === normalized && coupon.active !== false
        ) || null;
    },
    createDealProductQuickly() {
      if (!this.canQuickCreateProduct) {
        return;
      }

      const name = String(this.dealNewProduct.name || '').trim();
      const sku = String(this.dealNewProduct.sku || '').trim();

      this.$store
        .dispatch('products/create', {
          accountId: this.currentAccountId,
          product: {
            name,
            sku,
            price: Number(this.dealNewProduct.price || 0),
            active: true,
          },
        })
        .then(() => {
          this.$store
            .dispatch('products/get', {
              accountId: this.currentAccountId,
            })
            .catch(() => null);
          this.showCreateDealProductForm = false;
          this.dealNewProduct = { name: '', sku: '', price: '' };
        })
        .catch(() => null);
    },
    createProductsSnapshot() {
      this.productsDraftSnapshot = JSON.parse(
        JSON.stringify({
          selectedProducts: this.selectedProducts,
          couponCode: this.couponCode,
          selectedCoupon: this.selectedCoupon,
          manualDiscount: this.manualDiscount,
          surcharge: this.surcharge,
          shipping: this.shipping,
        })
      );
    },
    restoreProductsSnapshot() {
      if (!this.productsDraftSnapshot) return;
      this.selectedProducts = this.productsDraftSnapshot.selectedProducts || [];
      this.couponCode = this.productsDraftSnapshot.couponCode || '';
      this.selectedCoupon = this.productsDraftSnapshot.selectedCoupon || null;
      this.manualDiscount = this.productsDraftSnapshot.manualDiscount || 0;
      this.surcharge = this.productsDraftSnapshot.surcharge || 0;
      this.shipping = this.productsDraftSnapshot.shipping || 0;
    },
    openAttributesModal() {
      // Buscar deal atualizado do store se possível
      const getDeal = this.$store.getters['deals/getDeal'];
      const storeDeal =
        this.deal?.id && typeof getDeal === 'function'
          ? getDeal(this.deal.id) || this.deal
          : this.deal;
      const attrs = storeDeal?.custom_attributes || {};
      this.localCustomAttributes = {};
      this.dealAttributeDefinitions.forEach(def => {
        const v = attrs[def.attribute_key];
        this.localCustomAttributes[def.attribute_key] =
          v !== undefined && v !== null ? v : '';
      });
      this.showAttributesModal = true;
    },
    cancelAttributesModal() {
      this.showAttributesModal = false;
    },
    async saveAttributes() {
      if (!this.deal?.id) {
        this.showAttributesModal = false;
        return;
      }
      const existing = this.deal?.custom_attributes || {};
      // Filtrar chaves com valor vazio para não sobrescrever com ''
      const updates = Object.fromEntries(
        Object.entries(this.localCustomAttributes).filter(
          ([, v]) => v !== '' && v !== null && v !== undefined
        )
      );
      const merged = { ...existing, ...updates };
      try {
        await this.$store.dispatch('deals/update', {
          accountId: this.currentAccountId,
          dealId: this.deal.id,
          deal: { custom_attributes: merged },
        });
        this.showAttributesModal = false;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[saveAttributes] erro:', e?.response?.data || e);
      }
    },
    openProductsModal() {
      this.createProductsSnapshot();
      this.productsView = 'summary';
      this.showCreateDealProductForm = false;
      this.showProductsModal = true;
    },
    cancelProductsModal() {
      this.restoreProductsSnapshot();
      this.closeProductsModal();
    },
    saveProductsModal() {
      this.productsDraftSnapshot = null;
      this.closeProductsModal();
    },
    closeProductsModal() {
      this.showProductsModal = false;
      this.productsView = 'summary';
      this.showCreateDealProductForm = false;
    },
    onContactSelect(contactId) {
      this.localDeal.contact_id = contactId;
      this.localDeal.conversation_id = null;
      if (contactId) {
        this.$store.dispatch('contactConversations/get', contactId);
      }
    },
    handleSubmit() {
      const safeAmount = this.toSafeNumber(this.finalTotal, { min: 0 });
      const safeSubtotalProducts = this.toSafeNumber(this.subtotalProducts, {
        min: 0,
      });
      const safeCouponDiscount = this.toSafeNumber(this.couponDiscount, {
        min: 0,
      });
      const safeManualDiscount = this.toSafeNumber(this.manualDiscount, {
        min: 0,
      });
      const safeSurcharge = this.toSafeNumber(this.surcharge, { min: 0 });
      const safeShipping = this.toSafeNumber(this.shipping, { min: 0 });

      const sanitizedSelectedProducts = this.selectedProducts
        .map(item => {
          const productId = this.toSafeNumber(item.product_id, { min: 0 });
          return {
            deal_product_id: item.deal_product_id || null,
            product_id: productId,
            quantity: this.toSafeNumber(item.quantity || 1, { min: 1 }),
            unit_price: this.toSafeNumber(item.unit_price || 0, { min: 0 }),
          };
        })
        .filter(item => item.product_id > 0);

      const dealData = {
        title: this.localDeal.title,
        description: this.localDeal.description,
        amount: safeAmount,
        pipeline_id: this.localDeal.pipeline_id || this.pipelineId,
        pipeline_stage_id: this.localDeal.pipeline_stage_id,
        priority:
          this.localDeal.priority === 'none' ? null : this.localDeal.priority,
        close_date: this.localDeal.close_date,
        contact_id: this.localDeal.contact_id,
        conversation_id: this.localDeal.conversation_id || null,
        assignee_id: this.localDeal.responsible_agent_ids[0] || null,
        label_list: this.localDeal.label_list || [],
        amount_from_products: false,
        selected_products: sanitizedSelectedProducts,
        custom_attributes: {
          ...(() => {
            const getDeal = this.$store.getters['deals/getDeal'];
            const storeDeal =
              this.deal?.id && typeof getDeal === 'function'
                ? getDeal(this.deal.id) || this.deal
                : this.deal;
            return storeDeal?.custom_attributes || {};
          })(),
          start_date: this.localDeal.start_date || null,
          responsible_agent_ids: this.localDeal.responsible_agent_ids || [],
          subtotal_products: safeSubtotalProducts,
          coupon_code: this.selectedCoupon?.code || null,
          coupon_discount: safeCouponDiscount,
          manual_discount: safeManualDiscount,
          surcharge: safeSurcharge,
          shipping: safeShipping,
          calculated_total: safeAmount,
        },
      };
      this.$emit('submit', dealData);
    },
    onClose() {
      this.closeProductsModal();
      this.$emit('close');
      this.resetForm();
    },
    resetForm() {
      this.localDeal = {
        title: '',
        pipeline_stage_id: this.stages?.[0]?.id || '',
        priority: 'none',
        close_date: null,
        start_date: null,
        description: '',
        pipeline_id: this.pipelineId,
        contact_id: null,
        conversation_id: null,
        responsible_agent_ids: [],
        label_list: [],
      };
      this.selectedProducts = [];
      this.productSearch = '';
      this.couponCode = '';
      this.selectedCoupon = null;
      this.manualDiscount = 0;
      this.surcharge = 0;
      this.shipping = 0;
      this.showProductsModal = false;
      this.productsView = 'summary';
      this.showCreateDealProductForm = false;
      this.dealNewProduct = { name: '', sku: '', price: '' };
      this.productsDraftSnapshot = null;
      this.productsModalSaving = false;
      this.showAttributesModal = false;
      this.localCustomAttributes = {};
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <woot-modal :show="show" :on-close="onClose" size="medium">
    <div class="column content-box overflow-x-hidden">
      <woot-modal-header
        :header-title="deal ? $t('DEALS.EDIT.TITLE') : $t('DEALS.CREATE.TITLE')"
        :header-content="deal ? $t('DEALS.EDIT.DESC') : $t('DEALS.CREATE.DESC')"
      />

      <form class="space-y-4 overflow-x-hidden" @submit.prevent="handleSubmit">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
          <div class="space-y-2 min-w-0 flex flex-col">
            <div class="block !mb-0">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.TITLE.LABEL') }} *
              </span>
              <input
                v-model="localDeal.title"
                type="text"
                :placeholder="$t('DEALS.FORM.TITLE.PLACEHOLDER')"
                class="w-full h-10 !mt-0 !mb-0 px-3 py-0 leading-10 text-sm border border-n-weak rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors"
                required
              />
            </div>

            <div class="grid grid-cols-2 gap-2">
              <div class="block !mb-0">
                <span class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('DEALS.CONVERSATION.DEAL_MODAL.PIPELINE_LABEL') }} *
                </span>
                <ComboBox
                  :model-value="localDeal.pipeline_id"
                  :options="pipelineOptions"
                  :placeholder="
                    $t('DEALS.CONVERSATION.DEAL_MODAL.PIPELINE_PLACEHOLDER')
                  "
                  :search-placeholder="
                    $t('DEALS.FORM.PIPELINE.SEARCH_PLACEHOLDER')
                  "
                  :empty-state="$t('DEALS.FORM.PIPELINE.EMPTY_STATE')"
                  class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                  @update:model-value="val => (localDeal.pipeline_id = val)"
                />
              </div>
              <div class="block !mb-0">
                <span class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('DEALS.FORM.STAGE.LABEL') }} *
                </span>
                <ComboBox
                  :model-value="localDeal.pipeline_stage_id"
                  :options="stageOptions"
                  :placeholder="$t('DEALS.FORM.STAGE.PLACEHOLDER')"
                  :search-placeholder="
                    $t('DEALS.FORM.STAGE.SEARCH_PLACEHOLDER')
                  "
                  :empty-state="$t('DEALS.FORM.STAGE.EMPTY_STATE')"
                  class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                  @update:model-value="
                    val => (localDeal.pipeline_stage_id = val)
                  "
                />
              </div>
            </div>

            <div class="flex flex-col flex-1 min-h-[16rem]">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.DESCRIPTION.LABEL') }}
              </span>
              <Editor
                v-model="localDeal.description"
                editor-key="deal-description-pipeline"
                :placeholder="$t('DEALS.FORM.DESCRIPTION.PLACEHOLDER')"
                :max-length="5000"
                :show-character-count="false"
                class="deal-description-editor flex-1"
              />
            </div>
          </div>

          <div class="space-y-2 min-w-0">
            <label class="block !mb-0">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.CONTACT.LABEL') }} *
              </span>
              <ComboBox
                :model-value="localDeal.contact_id"
                :options="contactOptions"
                :placeholder="$t('DEALS.FORM.CONTACT.PLACEHOLDER')"
                :search-placeholder="
                  $t('DEALS.FORM.CONTACT.SEARCH_PLACEHOLDER')
                "
                :empty-state="$t('DEALS.FORM.CONTACT.EMPTY_STATE')"
                class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                @update:model-value="onContactSelect"
              />
            </label>

            <label class="block !mb-0">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.CONVERSATION.LABEL') }}
              </span>
              <ComboBox
                :model-value="localDeal.conversation_id"
                :options="conversationOptions"
                :disabled="!localDeal.contact_id"
                :placeholder="$t('DEALS.FORM.CONVERSATION.PLACEHOLDER')"
                :search-placeholder="
                  $t('DEALS.FORM.CONVERSATION.SEARCH_PLACEHOLDER')
                "
                :empty-state="$t('DEALS.FORM.CONVERSATION.EMPTY_STATE')"
                class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                @update:model-value="val => (localDeal.conversation_id = val)"
              />
            </label>

            <label class="block !mb-0">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.ASSIGNEE.LABEL') }}
              </span>
              <TagMultiSelectComboBox
                v-model="localDeal.responsible_agent_ids"
                :options="assigneeOptions"
                :placeholder="$t('DEALS.FORM.ASSIGNEE.PLACEHOLDER')"
                :search-placeholder="
                  $t('DEALS.FORM.ASSIGNEE.SEARCH_PLACEHOLDER')
                "
                :empty-state="$t('DEALS.FORM.ASSIGNEE.EMPTY_STATE')"
                use-fixed-dropdown
              />
            </label>

            <label class="block !mb-0">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.LABELS.LABEL') }}
              </span>
              <TagMultiSelectComboBox
                v-model="localDeal.label_list"
                :options="labelOptions"
                :placeholder="$t('DEALS.FORM.LABELS.PLACEHOLDER')"
                :search-placeholder="$t('DEALS.FORM.LABELS.SEARCH_PLACEHOLDER')"
                :empty-state="$t('DEALS.FORM.LABELS.EMPTY_STATE')"
                use-fixed-dropdown
              />
            </label>

            <label class="block !mb-0">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('DEALS.FORM.PRIORITY.LABEL') }}
              </span>
              <ComboBox
                :model-value="localDeal.priority"
                :options="priorityOptions"
                :placeholder="$t('DEALS.FORM.PRIORITY.PLACEHOLDER')"
                :search-placeholder="
                  $t('DEALS.FORM.PRIORITY.SEARCH_PLACEHOLDER')
                "
                :empty-state="$t('DEALS.FORM.PRIORITY.EMPTY_STATE')"
                class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
                @update:model-value="val => (localDeal.priority = val)"
              />
            </label>

            <div class="grid grid-cols-2 gap-2">
              <label class="block !mb-0">
                <span class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('DEALS.FORM.START_DATE.LABEL') }}
                </span>
                <input
                  v-model="localDeal.start_date"
                  type="date"
                  class="w-full px-3 py-2.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors"
                />
              </label>
              <label class="block !mb-0">
                <span class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ $t('DEALS.FORM.CLOSE_DATE.LABEL') }}
                </span>
                <input
                  v-model="localDeal.close_date"
                  type="date"
                  class="w-full px-3 py-2.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors"
                />
              </label>
            </div>

            <div class="space-y-1">
              <button
                type="button"
                class="w-full rounded-lg bg-n-brand/10 text-n-brand text-sm font-medium py-2.5 border border-n-brand/20 hover:bg-n-brand/15 transition"
                @click="openProductsModal"
              >
                {{ $t('DEALS.CONVERSATION.DEAL_MODAL.CONFIGURE_PRODUCTS') }}
              </button>
              <div
                class="flex items-center justify-between text-xs text-n-slate-11 px-1"
              >
                <span>{{ selectedProducts.length }} produto(s)</span>
                <span>Total: {{ formatCurrency(finalTotal) }}</span>
              </div>
            </div>

            <div
              v-if="deal && dealAttributeDefinitions.length"
              class="space-y-1"
            >
              <button
                type="button"
                class="w-full rounded-lg bg-n-alpha-2 text-n-slate-12 text-sm font-medium py-2.5 border border-n-weak hover:bg-n-alpha-3 transition"
                @click="openAttributesModal"
              >
                Gerenciar atributos personalizados
              </button>
              <div class="text-xs text-n-slate-11 px-1">
                {{ dealAttributeDefinitions.length }} atributo(s) disponível(is)
              </div>
            </div>
          </div>
        </div>

        <!-- Botoes -->
        <div
          class="flex items-center justify-between w-full gap-3 mt-6 pt-4 border-t border-n-weak"
        >
          <Button
            type="button"
            :label="$t('DEALS.FORM.CANCEL')"
            variant="link"
            class="h-10 hover:!no-underline hover:text-n-brand"
            @click="onClose"
          />
          <Button
            type="submit"
            :label="
              deal ? $t('DEALS.FORM.UPDATE_DEAL') : $t('DEALS.FORM.CREATE_DEAL')
            "
            color="blue"
            :disabled="!canSubmitDeal"
          />
        </div>
      </form>
    </div>
  </woot-modal>

  <woot-modal
    :show="showProductsModal"
    :on-close="cancelProductsModal"
    size="medium"
  >
    <div class="column content-box">
      <woot-modal-header
        header-title="Configurar produtos e valores"
        header-content="Adicione produtos, cupom e ajustes de valor"
      />

      <div class="modal-content w-full !pt-6 space-y-3">
        <template v-if="productsView === 'summary'">
          <div class="max-h-56 overflow-y-auto space-y-2 pr-1">
            <div
              v-if="!selectedProducts.length"
              class="text-center py-6 text-sm text-n-slate-12"
            >
              Nenhum produto adicionado.
            </div>
            <div
              v-for="item in selectedProducts"
              :key="`selected-${item.product_id}`"
              class="border-b border-n-weak pb-2 last:border-0 last:pb-0"
            >
              <div class="flex items-center justify-between gap-2">
                <p class="text-sm text-n-slate-12 truncate">{{ item.name }}</p>
                <span class="text-sm font-medium text-n-slate-12">{{
                  formatCurrency(item.unit_price)
                }}</span>
              </div>
              <div class="mt-1 flex items-center justify-between gap-2">
                <p class="text-xs text-n-slate-11 truncate">
                  {{ item.sku || '-' }}
                </p>
                <div
                  class="inline-flex items-center border border-n-weak rounded"
                >
                  <button
                    type="button"
                    class="px-2 py-0.5 text-n-slate-12"
                    @click="decreaseProductQty(item)"
                  >
                    -
                  </button>
                  <span class="px-2 text-sm text-n-slate-12">{{
                    item.quantity
                  }}</span>
                  <button
                    type="button"
                    class="px-2 py-0.5 text-n-slate-12"
                    @click="increaseProductQty(item)"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="text-right">
            <Button
              type="button"
              label="Adicionar produto"
              variant="link"
              class="h-8 !px-0 hover:!no-underline hover:text-n-brand"
              @click="productsView = 'catalog'"
            />
          </div>

          <div
            class="border-t border-n-weak pt-2 space-y-1.5 text-sm text-n-slate-12"
          >
            <div class="flex justify-between">
              <span>Total de produtos</span>
              <span class="font-medium">{{
                formatCurrency(subtotalProducts)
              }}</span>
            </div>
            <div class="flex items-center justify-between gap-2">
              <span>Cupom</span>
              <input
                v-model="couponCode"
                type="text"
                class="w-40 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                placeholder="Codigo do cupom"
                @blur="applyCoupon"
              />
            </div>
            <div class="flex items-center justify-between gap-2">
              <span>Desconto (-)</span>
              <input
                v-model.number="manualDiscount"
                type="number"
                min="0"
                step="0.01"
                class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                placeholder="Desconto"
              />
            </div>
            <div class="flex items-center justify-between gap-2">
              <span>Acrescimo (+)</span>
              <input
                v-model.number="surcharge"
                type="number"
                min="0"
                step="0.01"
                class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                placeholder="Acrescimo"
              />
            </div>
            <div class="flex items-center justify-between gap-2">
              <span>Frete (+)</span>
              <input
                v-model.number="shipping"
                type="number"
                min="0"
                step="0.01"
                class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                placeholder="Frete"
              />
            </div>
          </div>

          <div class="shrink-0 pt-3 space-y-2">
            <div
              class="flex items-center justify-between text-base font-semibold text-n-slate-12"
            >
              <span>Total</span>
              <span>{{ formatCurrency(finalTotal) }}</span>
            </div>

            <div class="flex items-center justify-between w-full gap-3">
              <Button
                label="Cancelar"
                variant="link"
                type="button"
                class="h-10 hover:!no-underline hover:text-n-brand"
                @click="cancelProductsModal"
              />
              <Button
                type="button"
                label="Salvar"
                color="blue"
                :disabled="!canSaveProductsModal || productsModalSaving"
                :is-loading="productsModalSaving"
                @click="saveProductsModal"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <input
            v-model="productSearch"
            type="text"
            class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
            placeholder="Buscar produto..."
          />
          <div
            class="max-h-56 overflow-y-auto border border-n-weak rounded-lg py-1"
          >
            <button
              v-for="product in filteredCatalogProducts"
              :key="`catalog-${product.id}`"
              type="button"
              class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="addProductToProductsModal(product)"
            >
              {{ product.name }}
            </button>
            <p
              v-if="!filteredCatalogProducts.length"
              class="px-3 py-2 text-sm text-n-slate-12"
            >
              Nenhum produto encontrado
            </p>
          </div>

          <div class="flex items-center justify-between">
            <Button
              type="button"
              label="Voltar"
              variant="link"
              class="h-8 !px-0 hover:!no-underline hover:text-n-brand"
              @click="productsView = 'summary'"
            />
            <Button
              type="button"
              label="Criar"
              variant="link"
              class="h-8 !px-0 hover:!no-underline hover:text-n-brand"
              @click="showCreateDealProductForm = !showCreateDealProductForm"
            />
          </div>

          <div
            v-if="showCreateDealProductForm"
            class="space-y-2 border border-n-weak rounded-lg p-2"
          >
            <input
              v-model="dealNewProduct.name"
              type="text"
              class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
              placeholder="Nome do produto"
            />
            <input
              v-model="dealNewProduct.sku"
              type="text"
              class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
              placeholder="SKU"
            />
            <input
              v-model="dealNewProduct.price"
              type="number"
              min="0"
              step="0.01"
              class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
              placeholder="Preco"
            />
            <div class="flex items-center justify-between w-full gap-3">
              <Button
                label="Cancelar"
                variant="link"
                type="button"
                class="h-10 hover:!no-underline hover:text-n-brand"
                @click="showCreateDealProductForm = false"
              />
              <Button
                type="button"
                label="Salvar produto"
                color="blue"
                :disabled="!canQuickCreateProduct"
                @click="createDealProductQuickly"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </woot-modal>

  <!-- Modal de atributos personalizados do negócio -->
  <woot-modal
    :show="showAttributesModal"
    :on-close="cancelAttributesModal"
    size="medium"
  >
    <div class="column content-box">
      <woot-modal-header
        header-title="Atributos personalizados"
        header-content="Gerencie os atributos deste negócio"
      />
      <div
        class="modal-content w-full !pt-6 space-y-3 max-h-[60vh] overflow-y-auto pr-1"
      >
        <div
          v-for="attr in dealAttributeDefinitions"
          :key="`attr-modal-${attr.attribute_key}`"
          class="rounded-lg border border-n-weak p-3 space-y-2"
        >
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ attr.attribute_display_name }}
            <span
              v-if="attr.attribute_description"
              class="ml-1 text-xs text-n-slate-11 font-normal"
            >
              — {{ attr.attribute_description }}
            </span>
          </label>

          <!-- text / link / phone -->
          <input
            v-if="
              ['text', 'link', 'phone_number'].includes(
                attr.attribute_display_type
              )
            "
            v-model="localCustomAttributes[attr.attribute_key]"
            type="text"
            class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
            :placeholder="attr.attribute_display_name"
          />

          <!-- number / currency -->
          <input
            v-else-if="
              ['number', 'currency'].includes(attr.attribute_display_type)
            "
            :value="localCustomAttributes[attr.attribute_key] ?? ''"
            type="number"
            class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
            @input="
              localCustomAttributes[attr.attribute_key] =
                $event.target.value === '' ? '' : Number($event.target.value)
            "
          />

          <!-- date -->
          <input
            v-else-if="attr.attribute_display_type === 'date'"
            v-model="localCustomAttributes[attr.attribute_key]"
            type="date"
            class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
          />

          <!-- list / select -->
          <ComboBox
            v-else-if="
              ['list', 'text_list', 'select'].includes(
                attr.attribute_display_type
              )
            "
            :model-value="
              localCustomAttributes[attr.attribute_key]
                ? String(localCustomAttributes[attr.attribute_key])
                : ''
            "
            :options="
              (attr.attribute_values || []).map(v => ({ label: v, value: v }))
            "
            placeholder="Selecione"
            use-fixed-dropdown
            class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
            @update:model-value="
              localCustomAttributes[attr.attribute_key] = $event
            "
          />

          <!-- checkbox -->
          <label
            v-else-if="attr.attribute_display_type === 'checkbox'"
            class="inline-flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              :checked="Boolean(localCustomAttributes[attr.attribute_key])"
              type="checkbox"
              @change="
                localCustomAttributes[attr.attribute_key] =
                  $event.target.checked
              "
            />
            Ativo
          </label>

          <!-- fallback -->
          <input
            v-else
            v-model="localCustomAttributes[attr.attribute_key]"
            type="text"
            class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
          />
        </div>

        <p
          v-if="!dealAttributeDefinitions.length"
          class="text-sm text-n-slate-11 text-center py-4"
        >
          Nenhum atributo personalizado cadastrado para negócios.
        </p>
      </div>
      <div
        class="flex items-center justify-end gap-3 pt-4 mt-2 border-t border-n-weak"
      >
        <Button
          type="button"
          label="Cancelar"
          variant="link"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="cancelAttributesModal"
        />
        <Button
          type="button"
          label="Salvar atributos"
          color="blue"
          @click="saveAttributes"
        />
      </div>
    </div>
  </woot-modal>
</template>

<style scoped lang="scss">
.deal-description-editor {
  display: flex;
  flex-direction: column;

  :deep(.editor-wrapper) {
    flex: 1;
    min-height: 0;
  }
}
</style>
